/**
 * ID Type Selection Guards
 *
 * Guards that check which ID type the patient selected.
 * Used to route between passport capture, ID card capture,
 * or skip/manual entry flows.
 */

import type { GuardWithMetadata } from "./sms.guard";

/**
 * Selected Passport Guard
 *
 * Checks if the patient selected "passport" as their ID type.
 * Routes to passport-specific photo capture flow.
 *
 * NOTE: Guards run BEFORE actions, so we check event.data (not context)
 */
export const selectedPassport: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context, event }: { context: any; event: any }) => {
    // Check event.data first (guards run before actions)
    if (event.data && typeof event.data.idType === "string") {
      return event.data.idType === "passport";
    }

    // Fallback to context (for state restoration)
    return context.idType === "passport";
  },
  metadata: {
    displayName: "selectedPassport",
    description: "Patient selected passport as their ID type",
    requiresContext: ["idType"],
    category: "context",
  },
};

/**
 * Selected ID Card Guard
 *
 * Checks if the patient selected "idCard" as their ID type.
 * Routes to ID card (driver's license) photo capture flow.
 *
 * NOTE: Guards run BEFORE actions, so we check event.data (not context)
 */
export const selectedIdCard: GuardWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
  impl: ({ context, event }: { context: any; event: any }) => {
    // Check event.data first (guards run before actions)
    if (event.data && typeof event.data.idType === "string") {
      return event.data.idType === "idCard";
    }

    // Fallback to context (for state restoration)
    return context.idType === "idCard";
  },
  metadata: {
    displayName: "selectedIdCard",
    description: "Patient selected ID card as their ID type",
    requiresContext: ["idType"],
    category: "context",
  },
};
